<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStoreGetters } from 'dashboard/composables/store';
import { AI_NAME } from 'dashboard/helper/aiIdentity';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AiIllustration from 'dashboard/components-next/ai/AiIllustration.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import OnboardingFeatureCard from './OnboardingFeatureCard.vue';

const getters = useStoreGetters();
const router = useRouter();
const { t } = useI18n();
const globalConfig = computed(() => getters['globalConfig/get'].value);
const currentUser = computed(() => getters.getCurrentUser.value);
const onboardingStepCount = 4;

const greetingMessage = computed(() => {
  const hours = new Date().getHours();
  const translationParams = {
    name: currentUser.value.name,
    installationName: globalConfig.value.installationName,
  };
  if (hours < 12) {
    return t('ONBOARDING.GREETING_MORNING', translationParams);
  }
  if (hours < 18) {
    return t('ONBOARDING.GREETING_AFTERNOON', translationParams);
  }
  return t('ONBOARDING.GREETING_EVENING', translationParams);
});
</script>

<template>
  <main class="onboarding mx-auto w-full max-w-7xl p-4 sm:p-8">
    <section class="onboarding-hero">
      <div class="relative z-10 min-w-0">
        <div
          class="mb-5 flex items-center gap-2 text-xs font-medium text-n-slate-11"
        >
          <span class="size-2 rounded-full bg-n-brand" aria-hidden="true" />
          <span>{{ globalConfig.installationName }}</span>
          <span class="h-3 w-px bg-n-slate-6" aria-hidden="true" />
          <span>{{ $t('ONBOARDING.EYEBROW') }}</span>
        </div>
        <p class="mb-2 text-sm font-medium text-n-slate-11 break-words">
          {{ greetingMessage }}
        </p>
        <h1 class="onboarding-title mb-3 font-semibold text-n-slate-12">
          {{ $t('ONBOARDING.HERO_TITLE') }}
        </h1>
        <p class="mb-6 max-w-xl text-sm leading-6 text-n-slate-11">
          {{
            $t('ONBOARDING.DESCRIPTION', {
              installationName: globalConfig.installationName,
            })
          }}
        </p>
        <NextButton
          class="onboarding-start"
          :label="$t('ONBOARDING.START')"
          icon="i-lucide-arrow-right"
          trailing-icon
          @click="router.push({ name: 'settings_inbox_new' })"
        />
      </div>
      <div class="onboarding-welcome" aria-hidden="true">
        <div class="onboarding-welcome-orbit" />
        <AiIllustration variant="welcome" class="relative z-10" />
        <span class="onboarding-welcome-label">{{ AI_NAME }}</span>
      </div>
    </section>

    <section class="mt-8" :aria-label="$t('ONBOARDING.STEPS_TITLE')">
      <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 class="m-0 text-base font-semibold text-n-slate-12">
          {{ $t('ONBOARDING.STEPS_TITLE') }}
        </h2>
        <span class="inline-flex items-center gap-2 text-xs text-n-slate-11">
          <Icon icon="i-lucide-route" class="size-4" aria-hidden="true" />
          {{ $t('ONBOARDING.STEP_COUNT', { count: onboardingStepCount }) }}
        </span>
      </div>
      <div class="onboarding-cards">
        <OnboardingFeatureCard
          step="01"
          illustration="channels"
          to="settings_inbox_new"
          :title="$t('ONBOARDING.ALL_CONVERSATION.TITLE')"
          :description="$t('ONBOARDING.ALL_CONVERSATION.DESCRIPTION')"
          :link-text="$t('ONBOARDING.ALL_CONVERSATION.NEW_LINK')"
        />
        <OnboardingFeatureCard
          step="02"
          illustration="team"
          to="agent_list"
          :title="$t('ONBOARDING.TEAM_MEMBERS.TITLE')"
          :description="$t('ONBOARDING.TEAM_MEMBERS.DESCRIPTION')"
          :link-text="$t('ONBOARDING.TEAM_MEMBERS.NEW_LINK')"
        />
        <OnboardingFeatureCard
          step="03"
          illustration="replies"
          to="canned_list"
          :title="$t('ONBOARDING.CANNED_RESPONSES.TITLE')"
          :description="$t('ONBOARDING.CANNED_RESPONSES.DESCRIPTION')"
          :link-text="$t('ONBOARDING.CANNED_RESPONSES.NEW_LINK')"
        />
        <OnboardingFeatureCard
          step="04"
          illustration="labels"
          to="labels_list"
          :title="$t('ONBOARDING.LABELS.TITLE')"
          :description="$t('ONBOARDING.LABELS.DESCRIPTION')"
          :link-text="$t('ONBOARDING.LABELS.NEW_LINK')"
        />
      </div>
    </section>
  </main>
</template>

<style scoped>
.onboarding {
  container-type: inline-size;
}
.onboarding-hero {
  display: grid;
  gap: 24px;
  overflow: hidden;
  position: relative;
  padding: 28px;
  border: 1px solid rgb(var(--slate-4));
  border-radius: 20px;
  background: radial-gradient(
      ellipse at top right,
      rgb(var(--blue-9) / 10%),
      transparent 65%
    ),
    rgb(var(--solid-1));
}
.onboarding-title {
  max-width: 540px;
  font-size: clamp(26px, 3cqi, 36px);
  line-height: 1.18;
  letter-spacing: -0.035em;
  overflow-wrap: anywhere;
}
.onboarding-welcome {
  display: none;
  position: relative;
  align-self: center;
  width: 210px;
  height: 208px;
}
.onboarding-welcome-orbit {
  position: absolute;
  inset: 8px;
  border: 1px solid rgb(var(--blue-9) / 14%);
  border-radius: 50%;
  background: rgb(var(--blue-9) / 4%);
}
.onboarding-welcome-label {
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  border: 1px solid rgb(var(--slate-4));
  border-radius: 99px;
  background: rgb(var(--solid-1));
  padding: 5px 16px;
  color: rgb(var(--slate-12));
  font-size: 12px;
  font-weight: 600;
}
.onboarding-cards {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
}
.onboarding-start :deep(.button-label) {
  white-space: normal;
  text-align: start;
}
@container (min-width: 620px) {
  .onboarding-hero {
    grid-template-columns: minmax(0, 1fr) 210px;
    padding: 32px;
  }
  .onboarding-welcome {
    display: block;
  }
  .onboarding-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@container (min-width: 1080px) {
  .onboarding-cards {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .onboarding-hero {
    padding: 36px 40px;
  }
}
</style>
